import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import './ProjectsList.css';
import ProjectCard from './ProjectCard/ProjectCard';
import { useProject } from '../../../contexts/ProjectContext';
import { useEC2InstanceStatus } from '../../../contexts/EC2InstanceStatusContext';
import RefreshButton from '../../RefreshButton/RefreshButton';
import useCachedApi from '../../../hooks/useCachedApi';

const FILTER_OPTIONS = [
  { value: 'all', label: 'Show All' },
  { value: 'rsa-model', label: 'RSA Model' },
  { value: 'rsa-existing', label: 'RSA Existing' },
  { value: 'visualization', label: 'Visualization' },
];

const SORT_OPTIONS = [
  { value: 'Name|Project Name|ASC', label: 'Project Name' },
  { value: 'Modified|Last update|DESC', label: 'Last update' },
  { value: 'ModelModified|Last model update|DESC', label: 'Last model update' },
  { value: 'LastViewed|Last Viewed|DESC', label: 'Last Viewed' },
  { value: 'UserRole|Role|ASC', label: 'Role' },
  { value: 'Size|Project Size|DESC', label: 'Project Size' },
];

const getProjectsCacheKey = (currentUser) => {
  const email =
    currentUser?.attributes?.email ||
    currentUser?.email ||
    currentUser?.Email ||
    sessionStorage.getItem('userEmail') ||
    'anonymous';
  return `projectsListCache:${String(email).trim().toLowerCase()}`;
};

const readProjectsCache = (cacheKey) => {
  try {
    const raw = sessionStorage.getItem(cacheKey);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed?.projects) ? parsed.projects : [];
  } catch (error) {
    console.warn('Failed to read projects list cache:', error);
    sessionStorage.removeItem(cacheKey);
    return [];
  }
};

const writeProjectsCache = (cacheKey, projectsToCache) => {
  try {
    const payload = {
      projects: projectsToCache,
      cachedAt: Date.now()
    };
    sessionStorage.setItem(cacheKey, JSON.stringify(payload));
  } catch (error) {
    console.warn('Failed to persist projects list cache:', error);
  }
};

const ProjectsList = ({ projects = [], loading, onProjectSelect, onViewProject, onRefresh, user, customRedirectPaths = {} }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all'); // 'all', 'rsa-model', 'rsa-existing', 'visualization'
  const [sortBy, setSortBy] = useState('Modified'); // 'Name', 'Modified', 'ModelModified', 'LastViewed', 'UserRole', 'Size'
  const [sortOrder, setSortOrder] = useState('DESC'); // 'ASC', 'DESC'
  const [sortOptionValue, setSortOptionValue] = useState('Modified|Last update|DESC');
  const [openDropdown, setOpenDropdown] = useState(null); // null | 'filter' | 'sort'
  const [isRefreshing, setIsRefreshing] = useState(false);
  const projectsCacheKey = useMemo(() => getProjectsCacheKey(user), [user]);
  const [cachedProjects, setCachedProjects] = useState(() => readProjectsCache(getProjectsCacheKey(user)));
  const [fetchProjects, setExistingProjectName] = useState("");
  const filterDropdownRef = useRef(null);
  const sortDropdownRef = useRef(null);
  
  // Use project context utilities
  const { formatDate, formatFileSize, isProjectExpired, canViewProject, canAccessProjectFiles } = useProject();
  
  // Use EC2 instance status context for polling
  const { startPolling, stopPolling, updateProjects } = useEC2InstanceStatus();
  
  // Memoize startPolling and stopPolling to prevent infinite loops
  const startPollingRef = useRef(startPolling);
  const stopPollingRef = useRef(stopPolling);
  
  useEffect(() => {
    startPollingRef.current = startPolling;
    stopPollingRef.current = stopPolling;
  }, [startPolling, stopPolling]);

  // Switch cache bucket when user changes.
  useEffect(() => {
    setCachedProjects(readProjectsCache(projectsCacheKey));
  }, [projectsCacheKey]);

  // Keep cache synced with fresh projects payload.
  useEffect(() => {
    if (Array.isArray(projects) && projects.length > 0) {
      setCachedProjects(projects);
      writeProjectsCache(projectsCacheKey, projects);
    }
  }, [projects, projectsCacheKey]);

  const projectsToRender = useMemo(() => {
    if (Array.isArray(projects) && projects.length > 0) {
      return projects;
    }
    return cachedProjects;
  }, [projects, cachedProjects]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        openDropdown === 'filter' && filterDropdownRef.current && !filterDropdownRef.current.contains(e.target) ||
        openDropdown === 'sort' && sortDropdownRef.current && !sortDropdownRef.current.contains(e.target)
      ) {
        setOpenDropdown(null);
      }
    };
    if (openDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [openDropdown]);

  // Debounce search term with shorter delay
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 50); // Reduced to 50ms delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Memoized filtering and sorting for better performance
  const filteredProjects = useMemo(() => {
    if (!projectsToRender || !Array.isArray(projectsToRender)) {
      return [];
    }
    
    let filtered = [...projectsToRender];

    // Apply search filter
    if (debouncedSearchTerm) {
      const searchLower = debouncedSearchTerm.toLowerCase();
      filtered = filtered.filter(project =>
        project.Name.toLowerCase().includes(searchLower) ||
        project.Plan.toLowerCase().includes(searchLower) ||
        project.UserRole.toLowerCase().includes(searchLower)
      );
    }

    // Apply type filter
    if (filterType !== 'all') {
      filtered = filtered.filter(project => project.ProjectType === filterType);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];

      // Handle date fields
      if (aValue instanceof Date && bValue instanceof Date) {
        aValue = aValue.getTime();
        bValue = bValue.getTime();
      }

      // Handle string fields
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (sortOrder === 'ASC') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });

    return filtered;
  }, [projectsToRender, debouncedSearchTerm, filterType, sortBy, sortOrder]);
  
  // Start/stop polling when projects change
  useEffect(() => {
    if (projectsToRender && projectsToRender.length > 0) {
      const projectIds = projectsToRender.map(p => String(p.Id));
      startPollingRef.current(projectIds);
    } else {
      stopPollingRef.current();
    }
    
    // Cleanup on unmount
    return () => {
      stopPollingRef.current();
    };
  }, [projectsToRender]); // Removed startPolling and stopPolling from dependencies to prevent infinite loop
  
  // Note: We don't need to update projects on filteredProjects change
  // The main projects list is already being polled via the projects useEffect above
  // Filtering/sorting is just a view concern, not a polling concern

  const handleSortChange = useCallback((value) => {
    const [field, label, order] = value.split('|');
    setSortBy(field);
    setSortOrder(order);
  }, []);

  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
  }, []);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setIsRefreshing(false);
    }
  };


  if (loading && projectsToRender.length === 0) {
    return (
      <div className="projects-list-loading">
        <div className="projects-list-loading-spinner"></div>
        Loading projects...
      </div>
    );
  }

  return (
    <div className="projects-list-container">
      {/* Header Controls */}
      <div className="projects-header">
        <div className="projects-controls">
          {/* Search */}
          <div className="search-container">
            <input
              type="search"
              placeholder="Search Projects"
              value={searchTerm}
              onChange={handleSearchChange}
              className="search-input"
            />
            <i className="fas fa-search search-icon"></i>
          </div>

          {/* Filters - custom dropdowns so open list has dark background */}
          <div className="filter-controls">
            <div className="filter-group filter-select-wrap" ref={filterDropdownRef}>
              <label>Show:</label>
              <button
                type="button"
                className="filter-select filter-select-trigger"
                onClick={() => setOpenDropdown(openDropdown === 'filter' ? null : 'filter')}
                aria-haspopup="listbox"
                aria-expanded={openDropdown === 'filter'}
              >
                <span>{FILTER_OPTIONS.find(o => o.value === filterType)?.label ?? 'Show All'}</span>
                <i className={`fas fa-chevron-down filter-select-chevron ${openDropdown === 'filter' ? 'open' : ''}`} />
              </button>
              {openDropdown === 'filter' && (
                <div className="filter-select-dropdown-panel" role="listbox">
                  {FILTER_OPTIONS.map((opt) => (
                    <div
                      key={opt.value}
                      role="option"
                      aria-selected={filterType === opt.value}
                      className={`filter-select-option ${filterType === opt.value ? 'selected' : ''}`}
                      onClick={() => { setFilterType(opt.value); setOpenDropdown(null); }}
                    >
                      {opt.label}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="filter-group filter-select-wrap" ref={sortDropdownRef}>
              <label>Sort by:</label>
              <button
                type="button"
                className="filter-select filter-select-trigger"
                onClick={() => setOpenDropdown(openDropdown === 'sort' ? null : 'sort')}
                aria-haspopup="listbox"
                aria-expanded={openDropdown === 'sort'}
              >
                <span>{SORT_OPTIONS.find(o => o.value === sortOptionValue)?.label ?? 'Last update'}</span>
                <i className={`fas fa-chevron-down filter-select-chevron ${openDropdown === 'sort' ? 'open' : ''}`} />
              </button>
              {openDropdown === 'sort' && (
                <div className="filter-select-dropdown-panel" role="listbox">
                  {SORT_OPTIONS.map((opt) => (
                    <div
                      key={opt.value}
                      role="option"
                      aria-selected={sortOptionValue === opt.value}
                      className={`filter-select-option ${sortOptionValue === opt.value ? 'selected' : ''}`}
                      onClick={() => {
                        setSortOptionValue(opt.value);
                        handleSortChange(opt.value);
                        setOpenDropdown(null);
                      }}
                    >
                      {opt.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>



          {/* View Mode Toggle */}
          <div className="view-controls">
            <RefreshButton
              onRefresh={handleRefresh}
              loading={isRefreshing}
              size="medium"
              variant="secondary"
              showText={false}
              className="projects-refresh-btn"
            />
          </div>
        </div>
      </div>

      {/* Projects Grid/List */}
      <div className="projects-content gallery">
        {isRefreshing ? (
          <div className="projects-list-loading">
            <div className="loading-spinner">
            </div>
            <p>Refreshing projects...</p>
          </div>
        ) : filteredProjects.length === 0 ? (
          <div className="no-projects">
            <i className="fas fa-folder-open"></i>
            <h3>No projects found</h3>
            <p>
              {debouncedSearchTerm || filterType !== 'all' 
                ? 'Try adjusting your search or filter criteria.' 
                : 'No projects available at the moment.'}
            </p>
          </div>
        ) : (
          <div
            className={`projects-grid gallery ${filteredProjects.length >= 3 ? 'fill-width' : ''}`.trim()}
          >
            {filteredProjects.map((project, index) => (
              <ProjectCard
                key={project.Id}
                project={project}
                onProjectSelect={onProjectSelect}
                onViewProject={onViewProject}
                formatDate={formatDate}
                user={user}
                customRedirectPath={customRedirectPaths[project.Id]}
                cardIndex={index}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectsList;
